import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Receipt, Plus, Search, Eye, CheckCircle, Building2, Calculator,
  DollarSign, TrendingUp, TrendingDown, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600",
  pending_approval: "bg-yellow-100 text-yellow-700",
  approved: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-500",
};

const FnFSettlement = () => {
  const { currentBusiness } = useAuth();
  const [settlements, setSettlements] = useState([]);
  const [exitRequests, setExitRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedFnF, setSelectedFnF] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    employee_id: "", exit_request_id: "", last_working_day: "",
    pending_salary: 0, leave_encashment: 0, leave_days_encashed: 0, bonus: 0,
    gratuity: 0, notice_pay: 0, other_earnings: 0, other_earnings_desc: "",
    notice_recovery: 0, advance_recovery: 0, asset_recovery: 0, other_deductions: 0,
    other_deductions_desc: "", pf_deduction: 0, tax_deduction: 0, notes: "",
  });

  const fetchSettlements = useCallback(async () => {
    if (!currentBusiness?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/fnf-settlements?business_id=${currentBusiness.id}`);
      setSettlements(res.data || []);
    } catch (err) {
      toast.error("Failed to load settlements");
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.id]);

  const fetchExitRequests = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      const res = await api.get(`/exit-requests?business_id=${currentBusiness.id}`);
      // Only exits without F&F yet
      setExitRequests((res.data || []).filter(e => !e.fnf_id && e.status !== "cancelled"));
    } catch (err) {
      console.error("Failed to load exit requests");
    }
  }, [currentBusiness?.id]);

  useEffect(() => {
    fetchSettlements();
    fetchExitRequests();
  }, [fetchSettlements, fetchExitRequests]);

  const resetForm = () => {
    setFormData({
      employee_id: "", exit_request_id: "", last_working_day: "",
      pending_salary: 0, leave_encashment: 0, leave_days_encashed: 0, bonus: 0,
      gratuity: 0, notice_pay: 0, other_earnings: 0, other_earnings_desc: "",
      notice_recovery: 0, advance_recovery: 0, asset_recovery: 0, other_deductions: 0,
      other_deductions_desc: "", pf_deduction: 0, tax_deduction: 0, notes: "",
    });
  };

  const calculateTotals = (data) => {
    const earnings = (parseFloat(data.pending_salary) || 0) +
      (parseFloat(data.leave_encashment) || 0) + (parseFloat(data.bonus) || 0) +
      (parseFloat(data.gratuity) || 0) + (parseFloat(data.notice_pay) || 0) +
      (parseFloat(data.other_earnings) || 0);
    const deductions = (parseFloat(data.notice_recovery) || 0) +
      (parseFloat(data.advance_recovery) || 0) + (parseFloat(data.asset_recovery) || 0) +
      (parseFloat(data.other_deductions) || 0) + (parseFloat(data.pf_deduction) || 0) +
      (parseFloat(data.tax_deduction) || 0);
    return { earnings, deductions, net: earnings - deductions };
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.exit_request_id) {
      toast.error("Select an exit request");
      return;
    }
    const exit = exitRequests.find(e => e.id === formData.exit_request_id);
    if (!exit) {
      toast.error("Invalid exit request");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/fnf-settlements", {
        ...formData,
        employee_id: exit.employee_id,
        business_id: currentBusiness.id,
      });
      toast.success("F&F settlement created");
      setShowAddDialog(false);
      resetForm();
      fetchSettlements();
      fetchExitRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create settlement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedFnF) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/fnf-settlements/${selectedFnF.id}`, formData);
      setSelectedFnF(res.data);
      toast.success("Settlement updated");
      fetchSettlements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoCalculate = async () => {
    if (!selectedFnF) return;
    try {
      const res = await api.post(`/fnf-settlements/${selectedFnF.id}/calculate`);
      setSelectedFnF(res.data);
      setFormData({
        ...formData,
        advance_recovery: res.data.advance_recovery,
        leave_encashment: res.data.leave_encashment,
        leave_days_encashed: res.data.leave_days_encashed,
        gratuity: res.data.gratuity,
      });
      toast.success("Auto-calculated values updated");
      fetchSettlements();
    } catch (err) {
      toast.error("Failed to calculate");
    }
  };

  const handleApprove = async () => {
    if (!selectedFnF) return;
    try {
      await api.post(`/fnf-settlements/${selectedFnF.id}/approve`);
      toast.success("Settlement approved");
      fetchSettlements();
      setShowViewDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedFnF) return;
    try {
      await api.post(`/fnf-settlements/${selectedFnF.id}/mark-paid`, {
        payment_mode: "bank_transfer",
        payment_reference: `PAY-${Date.now()}`,
      });
      toast.success("Marked as paid");
      fetchSettlements();
      setShowViewDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark as paid");
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const url = `${BACKEND_URL}/api/fnf-settlements/export?business_id=${currentBusiness.id}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `fnf_settlements_${new Date().toISOString().split("T")[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("F&F settlements exported");
    } catch (err) {
      toast.error("Failed to export");
    }
  };

  const openViewDialog = (fnf) => {
    setSelectedFnF(fnf);
    setFormData({
      pending_salary: fnf.pending_salary || 0,
      leave_encashment: fnf.leave_encashment || 0,
      leave_days_encashed: fnf.leave_days_encashed || 0,
      bonus: fnf.bonus || 0,
      gratuity: fnf.gratuity || 0,
      notice_pay: fnf.notice_pay || 0,
      other_earnings: fnf.other_earnings || 0,
      other_earnings_desc: fnf.other_earnings_desc || "",
      notice_recovery: fnf.notice_recovery || 0,
      advance_recovery: fnf.advance_recovery || 0,
      asset_recovery: fnf.asset_recovery || 0,
      other_deductions: fnf.other_deductions || 0,
      other_deductions_desc: fnf.other_deductions_desc || "",
      pf_deduction: fnf.pf_deduction || 0,
      tax_deduction: fnf.tax_deduction || 0,
      notes: fnf.notes || "",
    });
    setShowViewDialog(true);
  };

  const filteredSettlements = settlements.filter(s => {
    const matchesSearch = !search || 
      s.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.employee_code?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totals = calculateTotals(formData);

  const formatCurrency = (amount) => `₹${(parseFloat(amount) || 0).toLocaleString('en-IN')}`;

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to manage F&F settlements</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fnf-settlement-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Full & Final Settlement</h1>
          <p className="text-slate-500">Process employee settlements on exit</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={handleExport}
            data-testid="export-fnf-btn">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
          <Button className="btn-primary" onClick={() => { resetForm(); setShowAddDialog(true); }}
            data-testid="add-fnf-btn">
            <Plus className="h-4 w-4 mr-2" /> New Settlement
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-800">{settlements.length}</div>
            <div className="text-sm text-slate-500">Total Settlements</div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{settlements.filter(s => s.status === "pending_approval").length}</div>
            <div className="text-sm text-slate-500">Pending Approval</div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{settlements.filter(s => s.status === "approved").length}</div>
            <div className="text-sm text-slate-500">Approved</div>
          </CardContent>
        </Card>
        <Card className="card-soft">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{settlements.filter(s => s.status === "paid").length}</div>
            <div className="text-sm text-slate-500">Paid</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-soft">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search by name or code..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-10 input-field" data-testid="search-fnf" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="filter-fnf-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Settlements Table */}
      <Card className="card-soft">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : filteredSettlements.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <Receipt className="h-12 w-12 mb-3 text-slate-300" />
              <p>No settlements found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="font-semibold">Employee</TableHead>
                    <TableHead className="font-semibold">Last Working Day</TableHead>
                    <TableHead className="font-semibold text-right">Total Earnings</TableHead>
                    <TableHead className="font-semibold text-right">Total Deductions</TableHead>
                    <TableHead className="font-semibold text-right">Net Payable</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSettlements.map(fnf => (
                    <TableRow key={fnf.id} className="hover:bg-slate-50/50" data-testid={`fnf-row-${fnf.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{fnf.employee_name}</p>
                          <p className="text-xs text-slate-500">{fnf.employee_code}</p>
                        </div>
                      </TableCell>
                      <TableCell>{fnf.last_working_day || "-"}</TableCell>
                      <TableCell className="text-right text-green-600 font-medium">
                        {formatCurrency(fnf.total_earnings)}
                      </TableCell>
                      <TableCell className="text-right text-red-600 font-medium">
                        {formatCurrency(fnf.total_deductions)}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(fnf.net_payable)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[fnf.status]} capitalize`}>
                          {fnf.status?.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Button variant="ghost" size="icon" onClick={() => openViewDialog(fnf)}
                            data-testid={`view-fnf-${fnf.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Settlement Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New F&F Settlement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Exit Request *</Label>
              <Select value={formData.exit_request_id} onValueChange={v => {
                const exit = exitRequests.find(e => e.id === v);
                setFormData({...formData, exit_request_id: v, last_working_day: exit?.last_working_day || ""});
              }}>
                <SelectTrigger data-testid="fnf-exit-select">
                  <SelectValue placeholder="Select exit request" />
                </SelectTrigger>
                <SelectContent>
                  {exitRequests.length === 0 ? (
                    <div className="p-2 text-sm text-slate-500">No pending exits without F&F</div>
                  ) : (
                    exitRequests.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.employee_name} ({e.employee_code}) - {e.exit_type}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Last Working Day</Label>
              <Input type="date" value={formData.last_working_day} 
                onChange={e => setFormData({...formData, last_working_day: e.target.value})}
                data-testid="fnf-lwd-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes..." rows={2} data-testid="fnf-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" disabled={submitting} data-testid="create-fnf-btn">
                {submitting ? "Creating..." : "Create Settlement"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View/Edit Settlement Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>F&F Settlement Details</DialogTitle>
          </DialogHeader>
          {selectedFnF && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Employee</p>
                    <p className="font-medium">{selectedFnF.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Code</p>
                    <p className="font-medium">{selectedFnF.employee_code}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Status</p>
                    <Badge className={`${STATUS_COLORS[selectedFnF.status]} capitalize`}>
                      {selectedFnF.status?.replace("_", " ")}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Earnings */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" /> Earnings
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Pending Salary</Label>
                    <Input type="number" value={formData.pending_salary} 
                      onChange={e => setFormData({...formData, pending_salary: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-pending-salary" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Leave Encashment ({formData.leave_days_encashed} days)</Label>
                    <Input type="number" value={formData.leave_encashment} 
                      onChange={e => setFormData({...formData, leave_encashment: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-leave-encash" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Bonus</Label>
                    <Input type="number" value={formData.bonus} 
                      onChange={e => setFormData({...formData, bonus: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-bonus" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Gratuity</Label>
                    <Input type="number" value={formData.gratuity} 
                      onChange={e => setFormData({...formData, gratuity: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-gratuity" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Notice Pay</Label>
                    <Input type="number" value={formData.notice_pay} 
                      onChange={e => setFormData({...formData, notice_pay: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-notice-pay" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Other Earnings</Label>
                    <Input type="number" value={formData.other_earnings} 
                      onChange={e => setFormData({...formData, other_earnings: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-other-earnings" />
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-red-600" /> Deductions
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Notice Recovery</Label>
                    <Input type="number" value={formData.notice_recovery} 
                      onChange={e => setFormData({...formData, notice_recovery: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-notice-recovery" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Advance Recovery</Label>
                    <Input type="number" value={formData.advance_recovery} 
                      onChange={e => setFormData({...formData, advance_recovery: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-advance-recovery" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Asset Recovery</Label>
                    <Input type="number" value={formData.asset_recovery} 
                      onChange={e => setFormData({...formData, asset_recovery: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-asset-recovery" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">PF Deduction</Label>
                    <Input type="number" value={formData.pf_deduction} 
                      onChange={e => setFormData({...formData, pf_deduction: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-pf" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Tax Deduction</Label>
                    <Input type="number" value={formData.tax_deduction} 
                      onChange={e => setFormData({...formData, tax_deduction: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-tax" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Other Deductions</Label>
                    <Input type="number" value={formData.other_deductions} 
                      onChange={e => setFormData({...formData, other_deductions: parseFloat(e.target.value) || 0})}
                      disabled={selectedFnF.status === "paid"} data-testid="fnf-other-deductions" />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="p-4 bg-gradient-to-r from-violet-50 to-blue-50 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-slate-500">Total Earnings</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(totals.earnings)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Deductions</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(totals.deductions)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Net Payable</p>
                    <p className="text-2xl font-bold text-slate-800">{formatCurrency(totals.net)}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {selectedFnF.status !== "paid" && selectedFnF.status !== "cancelled" && (
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={handleAutoCalculate} data-testid="fnf-auto-calc-btn">
                    <Calculator className="h-4 w-4 mr-2" /> Auto Calculate
                  </Button>
                  <Button variant="outline" onClick={handleUpdate} disabled={submitting} data-testid="fnf-save-btn">
                    {submitting ? "Saving..." : "Save Changes"}
                  </Button>
                  {selectedFnF.status === "draft" && (
                    <Button className="btn-primary" onClick={async () => {
                      await api.put(`/fnf-settlements/${selectedFnF.id}`, { ...formData, status: "pending_approval" });
                      toast.success("Submitted for approval");
                      fetchSettlements();
                      setShowViewDialog(false);
                    }} data-testid="fnf-submit-approval-btn">
                      Submit for Approval
                    </Button>
                  )}
                  {selectedFnF.status === "pending_approval" && (
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleApprove}
                      data-testid="fnf-approve-btn">
                      <CheckCircle className="h-4 w-4 mr-2" /> Approve
                    </Button>
                  )}
                  {selectedFnF.status === "approved" && (
                    <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleMarkPaid}
                      data-testid="fnf-mark-paid-btn">
                      <DollarSign className="h-4 w-4 mr-2" /> Mark as Paid
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FnFSettlement;
